import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Loader2, X, History, ChevronDown, ChevronUp } from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { useTelegram } from '../../hooks';
import { showCombinedRewardedAds } from '../../hooks/useCombinedAds';
import { redeemApi, RedemptionHistoryItem } from '../../api';
import toast from 'react-hot-toast';

interface RedeemCodeProps {
  initialCode?: string | null;
}

export function RedeemCode({ initialCode }: RedeemCodeProps) {
  const [code, setCode] = useState('');
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<RedemptionHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  const { hapticFeedback, hapticNotification } = useTelegram();
  const updateBalance = useAppStore((state) => state.updateBalance);
  
  // Set initial code from deep link
  useEffect(() => {
    if (initialCode) {
      setCode(initialCode);
      hapticNotification('success');
      toast.success('Redeem code loaded! Tap Redeem to claim.', { duration: 3000 });
    }
  }, [initialCode, hapticNotification]);

  // Format code input (add hyphens automatically)
  const handleCodeChange = (value: string) => {
    // Remove non-alphanumeric except hyphens
    let cleaned = value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
    
    // Auto-insert hyphens every 4 characters if not present
    if (!cleaned.includes('-')) {
      const parts = cleaned.match(/.{1,4}/g);
      if (parts && parts.length > 1) {
        cleaned = parts.join('-');
      }
    }
    
    // Limit length
    if (cleaned.length <= 14) {
      setCode(cleaned);
    }
  };

  const handleRedeem = useCallback(async () => {
    if (!code.trim()) {
      toast.error('Please enter a redeem code');
      hapticNotification('error');
      return;
    }

    setIsRedeeming(true);
    hapticFeedback('medium');

    try {
      const response = await redeemApi.redeem(code.trim());

      if (response.success && response.amountRewarded !== undefined && response.newBalance !== undefined) {
        // Update balance in store
        updateBalance(response.newBalance);
        
        // Show success
        toast.success(
          <div className="text-center">
            <div className="font-bold">Code Redeemed! 🎉</div>
            <div className="text-sm">+{response.amountRewarded.toLocaleString()} points</div>
          </div>,
          { duration: 4000 }
        );
        hapticNotification('success');
        
        // Clear input
        setCode('');
        
        // Refresh history if loaded
        if (historyLoaded) {
          loadHistory();
        }

        // Show combined rewarded ads after successful redemption (non-blocking)
        setTimeout(async () => {
          try {
            toast.loading('Showing ads...', { id: 'redeem-ads' });
            await showCombinedRewardedAds('redeem_code');
            toast.dismiss('redeem-ads');
          } catch (error) {
            toast.dismiss('redeem-ads');
            // Silently ignore ad errors - it's a bonus, not required
            console.log('Ads not shown:', error);
          }
        }, 1500); // Small delay for better UX
      } else if (!response.success) {
        // Handle explicit failure from backend
        toast.error(response.error || 'Failed to redeem code');
        hapticNotification('error');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to redeem code';
      toast.error(errorMessage);
      hapticNotification('error');
    } finally {
      setIsRedeeming(false);
    }
  }, [code, hapticFeedback, hapticNotification, updateBalance, historyLoaded]);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await redeemApi.getHistory();
      if (response.success) {
        setHistory(response.redemptions || []);
        setHistoryLoaded(true);
      }
    } catch (error) {
      console.error('Failed to load redemption history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleHistory = () => {
    if (!showHistory && !historyLoaded) {
      loadHistory();
    }
    setShowHistory(!showHistory);
    hapticFeedback('light');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      {/* Main Card */}
      <div className="card overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 p-4 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Redeem Code</h3>
              <p className="text-sm text-white/60">Enter promo code to claim rewards</p>
            </div>
          </div>
        </div>

        {/* Input Section */}
        <div className="p-4 space-y-4">
          <div className="relative">
            <input
              type="text"
              value={code}
              onChange={(e) => handleCodeChange(e.target.value)}
              placeholder="XXXX-XXXX-XXXX"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-center text-xl font-mono tracking-widest uppercase placeholder:text-white/30 focus:border-purple-500/50 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-all"
              disabled={isRedeeming}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && code.trim()) {
                  handleRedeem();
                }
              }}
            />
            {code && (
              <button
                onClick={() => {
                  setCode('');
                  hapticFeedback('light');
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded-full transition-colors"
              >
                <X className="w-4 h-4 text-white/50" />
              </button>
            )}
          </div>

          <motion.button
            onClick={handleRedeem}
            disabled={isRedeeming || !code.trim()}
            whileTap={{ scale: 0.98 }}
            className={`w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
              isRedeeming || !code.trim()
                ? 'bg-white/10 text-white/40 cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40'
            }`}
          >
            {isRedeeming ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Redeeming...
              </>
            ) : (
              <>
                <Gift className="w-5 h-5" />
                Redeem Code
              </>
            )}
          </motion.button>

          <p className="text-center text-xs text-white/40">
            Codes are case-insensitive. Each code can only be used once per user.
          </p>
        </div>
      </div>

      {/* History Section */}
      <div className="card overflow-hidden">
        <button
          onClick={toggleHistory}
          className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-3">
            <History className="w-5 h-5 text-white/60" />
            <span className="font-medium">Redemption History</span>
            {historyLoaded && history.length > 0 && (
              <span className="px-2 py-0.5 bg-white/10 rounded-full text-xs">
                {history.length}
              </span>
            )}
          </div>
          {showHistory ? (
            <ChevronUp className="w-5 h-5 text-white/60" />
          ) : (
            <ChevronDown className="w-5 h-5 text-white/60" />
          )}
        </button>

        <AnimatePresence>
          {showHistory && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="border-t border-white/5">
                {historyLoading ? (
                  <div className="p-6 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-white/60" />
                  </div>
                ) : history.length === 0 ? (
                  <div className="p-6 text-center text-white/50">
                    <Gift className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No codes redeemed yet</p>
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto">
                    {history.map((item, index) => (
                      <div
                        key={`${item.code}-${index}`}
                        className="px-4 py-3 flex items-center justify-between border-b border-white/5 last:border-0"
                      >
                        <div>
                          <code className="text-sm font-mono bg-white/10 px-2 py-0.5 rounded">
                            {item.code}
                          </code>
                          <p className="text-xs text-white/50 mt-1">
                            {formatDate(item.redeemedAt)}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="text-green-400 font-semibold">
                            +{item.amountRewarded.toLocaleString()}
                          </span>
                          <p className="text-xs text-white/50">points</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
